import {
  CalendarMonth,
  Analytics as AnalyticsIcon,
  LocationOn as MapIcon,
  Assignment as AutoAssignIcon,
  CleaningServices,
  Group,
  PersonAdd,
  Email as EmailIcon,
  People as PeopleIcon,
  Assessment as LeadsIcon,
  HomeWork,
  Payment as PaymentIcon,
  CloudDownload as HCPImportIcon,
  Hotel as VacationRentalIcon,
  AccountBox,
  FolderOpen,
  Sms,
  Today as TodayIcon,
  Settings as SettingsIcon,
  Star as StarIcon,
  Download as DownloadIcon,
  ChecklistRtl as ChecklistIcon,
  History as HistoryIcon,
  RequestQuote as QuoteIcon,
  Campaign as CampaignIcon,
  AccountBalance as AccountingIcon,
  Sync as SyncIcon,
  CreditCard as SquarePaymentsIcon,
  ContactMail as ContactMailIcon,
  Description as DescriptionIcon,
  EventAvailable as ScheduleJobIcon,
} from '@mui/icons-material';

/**
 * Dashboard section and module configuration
 * Each section contains modules that can be navigated to or open dialogs
 */
export const DASHBOARD_SECTIONS = {
  dailyOperations: {
    id: 'daily-operations',
    title: 'Daily Operations',
    icon: TodayIcon,
    defaultExpanded: true,
    modules: [
      {
        id: 'service-schedule',
        name: 'Service Schedule',
        path: '/service-schedule',
        icon: CalendarMonth,
        description: 'View and manage service scheduling'
      },
      {
        id: 'schedule-job',
        name: 'Schedule a Job',
        action: 'scheduleJob',
        icon: ScheduleJobIcon,
        description: 'Create and schedule a job for a client'
      },
      {
        id: 'analytics',
        name: 'Analytics Dashboard',
        path: '/analytics',
        icon: AnalyticsIcon,
        description: 'View performance metrics and reports'
      },
      {
        id: 'maid-map',
        name: 'Technician Map',
        path: '/maid-map',
        icon: MapIcon,
        description: 'View technician locations and service areas'
      },
      {
        id: 'auto-assignment',
        name: 'Auto Assignment',
        action: 'autoAssignment',
        icon: AutoAssignIcon,
        description: 'Automatically assign technicians to jobs'
      },
      {
        id: 'job-history', // unchanged: pinned favorites are stored by id
        name: 'Jobs',
        path: '/jobs',
        icon: HistoryIcon,
        description: 'All jobs — upcoming and past — with payment and invoice status'
      }
    ]
  },
  userManagement: {
    id: 'user-management-section',
    title: 'User Management',
    icon: Group,
    defaultExpanded: false,
    modules: [
      {
        id: 'maid-management',
        name: 'Technician Management',
        path: '/maid-profile',
        icon: CleaningServices,
        description: 'Manage technician profiles and status'
      },
      {
        id: 'team-management',
        name: 'Team Management',
        path: '/team-management',
        icon: Group,
        description: 'Create and manage teams'
      },
      {
        id: 'invite-maid',
        name: 'Invite Technician',
        action: 'inviteMaid',
        icon: PersonAdd,
        description: 'Send professional invitation emails'
      },
      {
        id: 'invited-maids',
        name: 'Invited Technicians',
        action: 'invitedMaids',
        icon: EmailIcon,
        description: 'View sent invitation emails',
        badgeKey: 'invitationsCount'
      },
      {
        id: 'maid-broadcast',
        name: 'Notify Technicians in Area',
        action: 'maidBroadcast',
        icon: CampaignIcon,
        description: 'Email/text technicians within a radius of a job'
      },
      {
        id: 'bucket-documents',
        name: 'Bucket Documents',
        path: '/bucket-documents',
        icon: FolderOpen,
        description: 'Access S3 uploaded documents'
      },
      {
        id: 'user-management',
        name: 'System Users',
        path: '/user-management',
        icon: AccountBox,
        description: 'Manage system users and roles'
      }
    ]
  },
  customersLeads: {
    id: 'customers-leads',
    title: 'Customers & Leads',
    icon: PeopleIcon,
    defaultExpanded: false,
    modules: [
      {
        id: 'customer-management',
        name: 'Customer Management',
        path: '/customer-management',
        icon: PeopleIcon,
        description: 'View and manage customer profiles'
      },
      {
        id: 'quotations',
        name: 'Quotations',
        path: '/admin/quotations',
        icon: QuoteIcon,
        description: 'Review, preview and send quotes to customers'
      },
      {
        id: 'questionnaire-leads',
        name: 'Questionnaire Leads',
        path: '/questionnaire-leads',
        icon: LeadsIcon,
        description: 'Manage leads from questionnaire forms',
        badgeKey: 'leadsCount'
      },
      {
        id: 'property-manager-leads',
        name: 'Property Manager Leads',
        path: '/property-manager-leads',
        icon: ContactMailIcon,
        description: 'Outbound B2B prospects imported from CSV'
      },
      {
        id: 'lead-outreach',
        name: 'Lead Outreach',
        action: 'leadOutreach',
        icon: CampaignIcon,
        description: 'Email property managers near an understaffed technician'
      },
      {
        id: 'w9-campaign',
        name: 'W-9 Tax Campaign',
        path: '/w9-campaign',
        icon: DescriptionIcon,
        description: 'Request W-9 forms from contractors paid $600+ in a tax year'
      },
      {
        id: 'email-marketing',
        name: 'Email Marketing',
        action: 'emailMarketing',
        icon: EmailIcon,
        description: 'Send marketing emails to clients'
      },
      {
        id: 'client-outreach',
        name: 'Customer Outreach',
        action: 'clientOutreach',
        icon: CampaignIcon,
        description: 'Email all customers, or only those near an area'
      }
    ]
  },
  servicesPayments: {
    id: 'services-payments',
    title: 'Services & Payments',
    icon: PaymentIcon,
    defaultExpanded: false,
    modules: [
      {
        id: 'service-catalog',
        name: 'Service Catalog',
        path: '/service-catalog',
        icon: HomeWork,
        description: 'Manage cleaning services and pricing'
      },
      {
        id: 'payment-management',
        name: 'Payment Management',
        path: '/payment-management',
        icon: PaymentIcon,
        description: 'Track and manage technician payments'
      },
      {
        id: 'task-templates',
        name: 'Task Templates',
        path: '/task-templates',
        icon: ChecklistIcon,
        description: 'Manage job checklists by type and service'
      },
      {
        id: 'accounting',
        name: 'Accounting',
        externalUrl: process.env.REACT_APP_ODOO_URL || 'http://localhost:8069',
        icon: AccountingIcon,
        description: 'Invoices, AR aging, technician payables and P&L (Odoo)'
      }
    ]
  },
  integrationsTools: {
    id: 'integrations-tools',
    title: 'Integrations & Tools',
    icon: SettingsIcon,
    defaultExpanded: false,
    modules: [
      {
        id: 'hcp-import',
        name: 'HCP Job Import',
        action: 'hcpImport',
        icon: HCPImportIcon,
        description: 'Import jobs from HousecallPro'
      },
      {
        id: 'smoobu-import',
        name: 'Smoobu Import',
        action: 'smoobuImport',
        icon: VacationRentalIcon,
        description: 'Import vacation rental reservations'
      },
      {
        id: 'hcp-customer-import',
        name: 'HCP Customer Import',
        action: 'hcpCustomerImport',
        icon: DownloadIcon,
        description: 'Import all customers directly from HCP API'
      },
      {
        id: 'lead-import',
        name: 'Lead Import',
        action: 'leadImport',
        icon: ContactMailIcon,
        description: 'Import outbound B2B prospects from a CSV'
      },
      {
        id: 'odoo-sync',
        name: 'Odoo Sync',
        action: 'odooSync',
        icon: SyncIcon,
        description: 'Push customers, technicians and services to Odoo accounting'
      },
      {
        id: 'square-payments',
        name: 'Square Payments',
        action: 'squarePayments',
        icon: SquarePaymentsIcon,
        description: 'Connect your Square account for online and saved-card payments'
      },
      {
        id: 'sms-test',
        name: 'SMS Test',
        component: 'SMSTestCard',
        icon: Sms,
        description: 'Test SMS notifications'
      }
    ]
  }
};

// Pinned favorites section configuration
export const PINNED_SECTION = {
  id: 'pinned-favorites',
  title: 'Pinned Favorites',
  icon: StarIcon,
  defaultExpanded: true
};

/**
 * Get all modules as a flat array
 */
export const getAllModules = () => {
  return Object.values(DASHBOARD_SECTIONS).flatMap(section => section.modules);
};

/**
 * Get a module by its ID
 */
export const getModuleById = (moduleId) => {
  return getAllModules().find(module => module.id === moduleId);
};

/**
 * Calculate badge count for a section based on module badgeKeys
 */
export const calculateSectionBadge = (section, badgeCounts) => {
  if (!section?.modules || !badgeCounts) return 0;

  return section.modules.reduce((total, module) => {
    if (module.badgeKey && badgeCounts[module.badgeKey]) {
      return total + badgeCounts[module.badgeKey];
    }
    return total;
  }, 0);
};

/**
 * Get sections array in display order
 */
export const getSectionsArray = () => {
  return Object.values(DASHBOARD_SECTIONS);
};

/**
 * Map action names to dialog state keys
 */
export const ACTION_TO_DIALOG_MAP = {
  autoAssignment: 'autoAssignmentOpen',
  scheduleJob: 'scheduleJobOpen',
  inviteMaid: 'inviteDialogOpen',
  invitedMaids: 'invitedMaidsOpen',
  emailMarketing: 'emailToolOpen',
  hcpImport: 'hcpImportOpen',
  smoobuImport: 'smoobuImportOpen',
  hcpCustomerImport: 'hcpCustomerImportOpen',
  leadImport: 'leadImportOpen',
  leadOutreach: 'leadOutreachOpen',
  maidBroadcast: 'maidBroadcastOpen',
  clientOutreach: 'clientOutreachOpen',
  odooSync: 'odooSyncOpen',
  squarePayments: 'squarePaymentsOpen'
};
